<template>
  <div v-if="terms.length">
    <vue-final-modal
      v-model="showModal"
      classes="modal-container"
      content-class="modal-content w-600"
    >
      <div class="modal-title">
        <div class="modal-title-left">
          <img src="/images/close-icon.png" :alt="$t('vocabulary.close')" @click="toggleModal"/>
        </div>
        <h3>{{$t('vocabulary.title')}}</h3>
        <div class="modal-title-right"/>
      </div>
      <div class="modal-body vocabulary">
        <p class="vocabulary__hint">{{$t('vocabulary.hint')}}</p>
        <div class="vocabulary__list">
          <div
            v-for="(item, index) in terms"
            :key="`${item.term['zh-Hant']}-${index}`"
            class="vocabulary__item"
            :class="{ 'vocabulary__item--open': selectedIndex === index }"
          >
            <button
              type="button"
              class="vocabulary__heading"
              :aria-expanded="selectedIndex === index"
              @click="toggleTerm(index)"
            >
              <span>
                <strong>{{localizedValue(item.term)}}</strong>
                <small v-if="secondaryValue(item.term)">{{secondaryValue(item.term)}}</small>
              </span>
              <span class="vocabulary__indicator">{{selectedIndex === index ? '−' : '+'}}</span>
            </button>
            <div v-if="selectedIndex === index" class="vocabulary__content">
              <div class="vocabulary__definition">
                <p>
                  <strong>{{$t('vocabulary.chinese_definition')}}</strong>
                  {{item.definition['zh-Hant']}}
                </p>
                <p>
                  <strong>{{$t('vocabulary.english_definition')}}</strong>
                  {{item.definition.en}}
                </p>
              </div>
              <div class="vocabulary__speech">
                <button type="button" @click="speak(speechText(item, 'en'), 'en-US')">
                  🔊 {{$t('vocabulary.english')}}
                </button>
                <button type="button" @click="speak(speechText(item, 'zh-Hant'), 'zh-HK')">
                  🔊 {{$t('vocabulary.cantonese')}}
                </button>
                <button type="button" @click="speak(speechText(item, 'zh-Hant'), 'zh-CN')">
                  🔊 {{$t('vocabulary.mandarin')}}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </vue-final-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { defaultSpeechRate, findVoice, getVoices } from '@/utils/speech'

export default {
  name: 'VocabularyModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    terms: {
      type: Array,
      default: () => []
    }
  },
  emits: ['toggle'],
  data () {
    return {
      showModal: false,
      selectedIndex: null,
      voices: []
    }
  },
  computed: {
    ...mapGetters({
      language: 'auth/language'
    })
  },
  methods: {
    toggleModal () {
      this.showModal = false
    },
    toggleTerm (index) {
      this.selectedIndex = this.selectedIndex === index ? null : index
    },
    localizedValue (value) {
      return value[this.language] || value['zh-Hant'] || value.en || ''
    },
    secondaryValue (value) {
      const secondaryLanguage = this.language === 'en' ? 'zh-Hant' : 'en'
      const secondary = value[secondaryLanguage]
      return secondary && secondary !== this.localizedValue(value) ? secondary : ''
    },
    speechText (item, language) {
      return `${item.term[language]}. ${item.definition[language]}`
    },
    loadVoices () {
      this.voices = getVoices()
    },
    speak (text, language) {
      if (!text || !window.speechSynthesis || !window.SpeechSynthesisUtterance) {
        window.alert(this.$t('vocabulary.speech_unsupported'))
        return
      }

      this.loadVoices()
      const voice = findVoice(language, this.voices)
      if (!voice) {
        window.alert(this.$t('vocabulary.voice_unavailable'))
        return
      }

      window.speechSynthesis.cancel()
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.lang = voice.lang
      utterance.rate = defaultSpeechRate(language)
      utterance.pitch = 1
      utterance.voice = voice

      window.speechSynthesis.speak(utterance)
    }
  },
  mounted () {
    this.loadVoices()
    if (window.speechSynthesis) {
      window.speechSynthesis.addEventListener('voiceschanged', this.loadVoices)
    }
  },
  beforeUnmount () {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel()
      window.speechSynthesis.removeEventListener('voiceschanged', this.loadVoices)
    }
  },
  watch: {
    show (value) {
      this.showModal = value
      if (!value) {
        this.selectedIndex = null
        if (window.speechSynthesis) window.speechSynthesis.cancel()
      }
    },
    showModal (value) {
      this.$emit('toggle', value)
    },
    terms () {
      this.selectedIndex = null
    }
  }
}
</script>

<style lang="scss">
@import "style";
</style>
